const CAP_SOFT_HARD_FORKS = {
  "id": 75,
  "title": "Soft Forks y Hard Forks en Bitcoin",
  "slug": "soft-forks-y-hard-forks-en-bitcoin",
  "type": "lesson",
  "category": "bitcoin",
  "level": "intermediate",
  "sections": [
    {
      "heading": "Qué es un Soft Fork",
      "text": [
        "Un **Soft Fork** es una actualización del código del programa que gestiona una blockchain para añadir un cambio, normalmente una funcionalidad nueva o la corrección de algún error.",
        "Para que una actualización se considere un soft fork, el nuevo código debe seguir siendo **compatible con la blockchain antigua**.",
        "Es decir, las nuevas reglas son más estrictas, pero los bloques y transacciones que cumplen las reglas nuevas también son válidos para los nodos que todavía siguen con las reglas antiguas."
      ]
    },
    {
      "heading": "Compatibilidad y participación en la red",
      "text": [
        "En un soft fork, los participantes que **no actualizan** su software pueden seguir operando en la red.",
        "Estos nodos antiguos pueden seguir verificando bloques y transacciones sin ningún problema, aunque **no aprovechan las mejoras** introducidas por la actualización.",
        "Los nodos que sí se actualizan aplican las nuevas reglas, mientras que los que no lo hacen continúan validando de acuerdo con las reglas anteriores, pero ambos siguen formando parte de la misma cadena de bloques."
      ]
    },
    {
      "heading": "Soft Forks en Bitcoin y BIP",
      "text": [
        "En Bitcoin, los procesos de actualización del protocolo se anuncian primero a través de los **BIP (Bitcoin Improvement Proposals – Propuestas de Mejora de Bitcoin)**.",
        "Una vez que una propuesta es discutida y aceptada por la comunidad, la implementación posterior depende de los desarrolladores de los diferentes clientes de Bitcoin.",
        "Cada implementación integra el cambio en su código, respetando siempre que se mantenga la compatibilidad con la blockchain existente para que se considere un soft fork."
      ]
    },
    {
      "heading": "Tipos de Soft Fork",
      "text": [
        "Existen dos grandes formas de activar un Soft Fork en una red como Bitcoin, según quién haga cumplir las nuevas reglas."
      ],
      "list": [
        "🛠️ **Soft fork activado por mineros (MASF)**: se produce cuando la mayoría de los mineros se actualizan para hacer cumplir las nuevas reglas del protocolo. Al minar y propagar bloques bajo las nuevas reglas, van imponiendo de facto esas reglas al resto de la red.",
        "👥 **Soft fork activado por usuarios (UASF)**: se produce cuando los nodos completos (usuarios que ejecutan nodos) se coordinan para empezar a exigir las nuevas reglas, incluso sin contar inicialmente con el apoyo mayoritario de los mineros. Son los nodos quienes deciden qué bloques aceptar o rechazar."
      ]
    },
    {
      "heading": "Qué es un Hard Fork",
      "text": [
        "Un **Hard Fork** (bifurcación dura) es una actualización importante del protocolo que **hace incompatible** la nueva versión con las versiones anteriores.",
        "En un hard fork, los cambios en el código son tan grandes que los nodos que no se actualizan **ya no pueden seguir la misma cadena de bloques** que los nodos que sí han actualizado.",
        "Para seguir usando la misma red que adopta las nuevas reglas, todos los usuarios deben **migrar al nuevo software**. Si no lo hacen, continuarán en una cadena distinta."
      ]
    },
    {
      "heading": "Bifurcación de la red en un Hard Fork",
      "text": [
        "A diferencia de un Soft Fork, en un Hard Fork se produce un cambio que rompe la compatibilidad hacia atrás y provoca una **bifurcación de la red**.",
        "Desde ese momento, existen dos posibilidades claras:",
        "• Seguir usando la **nueva versión** del software, con las nuevas reglas.\n• Permanecer en la **versión anterior**, con las reglas antiguas.",
        "El resultado es que pueden existir **dos cadenas de bloques diferentes**, que comparten el mismo historial hasta el momento del fork, pero a partir de ahí siguen caminos separados."
      ]
    },
    {
      "heading": "Motivos de un Hard Fork",
      "text": [
        "Los motivos por los que se produce un Hard Fork pueden ser:",
        "• Motivos “accidentales”: correcciones importantes de errores (bugs) en el código que requieren cambios incompatibles con las versiones anteriores.\n• Motivos **premeditados**: la comunidad o una parte de ella diseña una nueva propuesta de protocolo con reglas diferentes (por ejemplo, cambiar el tamaño del bloque) y la aplica en una nueva versión del código.",
        "En ambos casos, el resultado es el mismo: si no todos adoptan las nuevas reglas, la red se divide."
      ]
    },
    {
      "heading": "Analogía del viaje por el bosque",
      "text": [
        "Para entender mejor qué es un Hard Fork, podemos imaginar un viaje con amigos por el bosque.",
        "Mientras todo el grupo sigue el mismo camino, **todos ven lo mismo** y comparten la misma experiencia. Esto equivale a una única cadena de bloques.",
        "Sin embargo, si en un punto del camino una parte del grupo decide seguir una ruta diferente, las experiencias cambian. A partir de ese cruce, lo que ve cada subgrupo ya no es igual.",
        "En un hard fork ocurre algo parecido: ambas cadenas **comparten el pasado**, pero desde el punto de bifurcación en adelante, cada una construye su propia historia independiente."
      ]
    },
    {
      "heading": "Dos cadenas y dos monedas: el caso de Bitcoin Cash",
      "text": [
        "Un Hard Fork puede originar **dos cadenas (la original y la nueva)**, dando lugar a dos redes y dos monedas diferentes.",
        "Esto ha ocurrido varias veces en la historia de Bitcoin. Uno de los Hard Fork más conocidos es **Bitcoin Cash**.",
        "Bitcoin Cash es una bifurcación buscada de Bitcoin con el objetivo de mejorar algunas características relacionadas con la capacidad de la red.",
        "Ambas redes comparten muchas características técnicas:\n• Utilizan el algoritmo de hash **SHA-256**.\n• Generan bloques aproximadamente cada **10 minutos**.",
        "Las diferencias clave están en parámetros como el **tamaño del bloque** y la capacidad de procesamiento de transacciones:",
        "• En **Bitcoin (BTC)** los bloques son de **1 MB** y permiten alrededor de **3,5 transacciones por segundo**.\n• En **Bitcoin Cash (BCH)** los bloques pueden llegar hasta **32 MB** y admitir alrededor de **24 transacciones por segundo**.",
        "A partir del Hard Fork, cada una de estas redes evoluciona por separado, con su propia comunidad, sus propias reglas y su propio mercado."
      ]
    },
   {
  "heading": "Tabla comparativa: Soft Fork vs Hard Fork",
  "table": {
    "columns": [
      "Característica",
      "Soft Fork",
      "Hard Fork"
    ],
    "rows": [
      {
        "Característica": "Compatibilidad",
        "Soft Fork": "Compatible con versiones anteriores (las nuevas reglas son más estrictas, pero válidas para nodos antiguos).",
        "Hard Fork": "No compatible con versiones anteriores (las nuevas reglas rompen la compatibilidad con nodos antiguos)."
      },
      {
        "Característica": "Cadena de bloques",
        "Soft Fork": "Mantiene una única cadena. No suele crear una nueva moneda.",
        "Hard Fork": "Puede dividir la cadena en dos y dar lugar a dos redes y monedas diferentes."
      },
      {
        "Característica": "Actualización de nodos",
        "Soft Fork": "La actualización no es obligatoria para seguir en la red, aunque es recomendable.",
        "Hard Fork": "La actualización es necesaria para seguir en la cadena nueva; los nodos antiguos quedan en otra cadena."
      },
      {
        "Característica": "Rol de los nodos antiguos",
        "Soft Fork": "Pueden seguir validando bloques y transacciones (aunque sin las nuevas mejoras).",
        "Hard Fork": "Ya no pueden validar ni seguir la cadena nueva; solo siguen la cadena antigua."
      },
      {
        "Característica": "Riesgo de bifurcación permanente",
        "Soft Fork": "Bajo: el objetivo es mantener una única cadena con reglas compatibles.",
        "Hard Fork": "Alto: puede consolidarse una división permanente en dos cadenas."
      },
      {
        "Característica": "Tipo de cambios",
        "Soft Fork": "Cambios más bien restrictivos o de ajuste fino, que estrechan las reglas existentes.",
        "Hard Fork": "Cambios estructurales o más profundos que modifican límites, formatos o reglas base."
      },
      {
        "Característica": "Ejemplo típico",
        "Soft Fork": "Activación de mejoras que se introducen sin romper la compatibilidad con bloques antiguos.",
        "Hard Fork": "Creación de una nueva moneda derivada de Bitcoin, como el caso de Bitcoin Cash."
      }
    ]
  }
},

    {
      "heading": "Cuándo se ha usado cada tipo de fork en Bitcoin",
      "list": [
        "🧩 **Soft forks en Bitcoin**: se han utilizado para introducir mejoras manteniendo la misma cadena. Ejemplos conocidos son la activación de nuevas reglas y características que no rompen la compatibilidad con los nodos que aún no se han actualizado.",
        "🔗 **Hard forks en Bitcoin**: se han utilizado cuando una parte de la comunidad ha querido cambiar reglas de forma incompatible, dando lugar a nuevas redes y monedas. El caso más conocido es **Bitcoin Cash (BCH)**, que surgió al separar su camino de la cadena original de Bitcoin para modificar parámetros como el tamaño del bloque."
      ]
    },
    {
  id: "comparativa-soft-hard-forks",
  heading: "Soft Fork vs Hard Fork en Bitcoin",
  text: `Comparación general entre Soft Forks y Hard Forks en el protocolo de Bitcoin.`,
  table: {
    columns: [
      "Tipo",
      "Compatibilidad",
      "Efecto en la cadena",
      "Actualización de nodos",
      "Ventajas",
      "Desventajas",
      "Ejemplos en Bitcoin / Uso"
    ],
    rows: [
      {
        Tipo: "Soft Fork",
        Compatibilidad:
          "Compatible hacia atrás: los bloques con las nuevas reglas siguen siendo válidos para los nodos que no se han actualizado.",
        "Efecto en la cadena":
          "Se mantiene una sola cadena. El objetivo es que toda la red continúe compartiendo el mismo historial.",
        "Actualización de nodos":
          "Recomendable pero no estrictamente obligatoria para seguir validando bloques; los nodos antiguos siguen participando aunque sin aprovechar las nuevas funciones.",
        Ventajas:
          "Menor riesgo de división permanente de la red; actualización más gradual; facilita la coordinación por BIP y activaciones progresivas.",
        Desventajas:
          "Puede ser más complejo de diseñar porque las nuevas reglas deben seguir siendo compatibles con las antiguas; requiere coordinación entre nodos y/o mineros.",
        "Ejemplos en Bitcoin / Uso":
          "Se han usado para introducir mejoras como reglas más estrictas o nuevas capacidades manteniendo compatibilidad. Ejemplos típicos: activaciones de mejoras definidas en BIP y aplicadas como soft fork, donde nodos actualizados empiezan a exigir nuevas reglas mientras los antiguos siguen aceptando bloques válidos."
      },
      {
        Tipo: "Hard Fork",
        Compatibilidad:
          "No compatible hacia atrás: las nuevas reglas hacen que los bloques de la nueva versión puedan no ser aceptados por los nodos antiguos.",
        "Efecto en la cadena":
          "Puede dividir la red en dos cadenas distintas (original y nueva), cada una con su propia historia a partir del punto de bifurcación.",
        "Actualización de nodos":
          "Necesaria si se quiere seguir en la nueva cadena; los nodos que no actualizan continúan en la versión antigua del protocolo.",
        Ventajas:
          "Permite cambios profundos en el protocolo (límites, tamaños de bloque, formato de datos, etc.); mayor libertad para rediseñar reglas sin necesidad de mantener compatibilidad.",
        Desventajas:
          "Alto riesgo de división de la comunidad y del mercado; pueden surgir dos monedas y dos ecosistemas separados; requiere coordinación social mucho más delicada.",
        "Ejemplos en Bitcoin / Uso":
          "Se ha usado cuando una parte de la comunidad ha querido cambiar reglas de forma incompatible. Ejemplo conocido: creación de Bitcoin Cash como nueva cadena con bloques más grandes y parámetros distintos respecto a la cadena de Bitcoin original."
      }
    ]
  }
}

  ],

};

export default CAP_SOFT_HARD_FORKS;
